/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

$(document).ready(function () {
  // sticky_header drives the full-header <-> condensed-bar swap. Give it a
  // dead-band (turn ON above 80, OFF below 30) instead of a single 50px cutoff.
  // Without the gap, tiny scroll jitter/momentum right at the threshold flips
  // the class on/off every frame, and because the swap is an instant display
  // change with a different header height, that reads as flicker. The 30–80 gap
  // also keeps this toggle from firing at the same scroll point as the
  // direction toggles below (which react around a 20px move).
  const stickyBody = document.body;
  $(window).on("scroll load", function () {
    const st = Math.max(0, $(this).scrollTop());
    const isSticky = stickyBody.classList.contains("sticky_header");
    if (!isSticky && st > 80) {
      stickyBody.classList.add("sticky_header");
    } else if (isSticky && st < 30) {
      stickyBody.classList.remove("sticky_header");
    }
  });


  let lastScrollTop = 0;
  const threshold = 20; // minimum scroll difference

  $(window).on("scroll", function () {
    let scrollTop = $(this).scrollTop();

    // iOS fix: Prevent negative scroll values
    scrollTop = Math.max(0, scrollTop);

    if (Math.abs(scrollTop - lastScrollTop) <= threshold) {
      // Don't do anything for very small scroll movements
      return;
    }

    if (scrollTop > lastScrollTop) {
      // Scroll down
      $("body").addClass("down-menu-sticky");
      $("body").removeClass("up-menu-sticky");
    } else {
      // Scroll up. Everywhere except product pages the full header (logo + menu) stays hidden while the shopper is
      // scrolled into the page – only the announcement bar (+ the condensed search/cart bar on phones) stays. It comes
      // back at the very top, where body.sticky_header is removed (below 30px) and the hide rules no longer apply.
      if (document.body.classList.contains("sticky_header") && !document.body.classList.contains("template-product")) {
        lastScrollTop = scrollTop;
        return;
      }
      $("body").addClass("up-menu-sticky");
      $("body").removeClass("down-menu-sticky");
    }

    lastScrollTop = scrollTop;
  });
});
// === PDP gallery wishlist heart — variant sync ===
// The Merkliste heart on the first photo (own wishlist, assets/tt-wishlist.js)
// saves the variant it carries, so keep it pointing at the selected size.
document.addEventListener("on:variant:change", (event) => {
  const heart = document.querySelector(".product-gallery__wishlist-btn");
  const variant = event.detail && event.detail.variant;
  if (heart && variant) heart.dataset.wlVariant = variant.id;
});

// === Low Stock Urgency Badge — product page ===
// Listens for variant selection changes and updates the low-stock badge
// without a page reload, using the pre-rendered inventory data attributes.
document.addEventListener("on:variant:change", (event) => {
  const badge = document.querySelector(
    ".low-stock-badge:not(.low-stock-badge--cart)",
  );
  if (!badge) return;

  const minThreshold = parseInt(badge.dataset.minThreshold, 10);
  const maxThreshold = parseInt(badge.dataset.maxThreshold, 10);
  const variant = event.detail && event.detail.variant;

  if (!variant) {
    badge.textContent = "";
    badge.classList.add("low-stock-badge--hidden");
    return;
  }

  // Read inventory from the pre-rendered .variant-details element
  const variantEl = document.querySelector(
    `.variant-details[var-d="${variant.id}"]`,
  );
  const qty = variantEl
    ? parseInt(variantEl.getAttribute("var-inv"), 10) || 0
    : 0;

  const productId = parseInt(badge.dataset.productId, 10) || 0;

  // Deterministic per product (matches Liquid): product.id % 10 >= 4 → visible (~60%)
  const skipRoll = productId % 10;
  const aboveThresholdVisible = skipRoll >= 4;

  const randRange = maxThreshold - minThreshold + 1;
  const randDisplay = (productId % randRange) + minThreshold;

  let displayQty = 0;
  let visible = false;

  if (qty > 0 && qty < maxThreshold) {
    // Real low stock: always show actual qty
    displayQty = qty;
    visible = true;
  } else if (qty >= maxThreshold && aboveThresholdVisible) {
    // High stock: show random qty in min–max range for ~70% of products
    displayQty = randDisplay;
    visible = true;
  }

  if (visible) {
    const messageTemplate =
      badge.dataset.stockMessage || badge.dataset.messageTemplate || "";
    badge.textContent = messageTemplate
      .replace("COUNT_PLACEHOLDER", displayQty)
      .replace("{{ count }}", displayQty);
    badge.classList.remove("low-stock-badge--hidden");

    // Keep hidden form input in sync for cart
    const form = event.detail && event.detail.form;
    if (form) {
      let input = form.querySelector('[name="properties[_low_stock_qty]"]');
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = "properties[_low_stock_qty]";
        form.appendChild(input);
      }
      input.value = displayQty;
    }
  } else {
    badge.textContent = "";
    badge.classList.add("low-stock-badge--hidden");

    const form = event.detail && event.detail.form;
    if (form) {
      const input = form.querySelector('[name="properties[_low_stock_qty]"]');
      if (input) input.parentNode.removeChild(input);
    }
  }
});

// === Judge.me masonry layout fix ===
// On initial page load images aren't loaded yet when Judge.me calculates
// absolute positions, causing large gaps. Triggering the sort select's
// change event after images are ready forces a full recalculation.
function jdgmTriggerRelayout() {
  var sortSelect = document.querySelector(
    ".jdgm-rev-widg__sort select, .jdgm-rev-widg__sort",
  );
  if (sortSelect) {
    sortSelect.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }
  return false;
}

// Try on Judge.me's own load event first, then fall back to image-load detection
document.addEventListener("jdgm:reviews_grid_widget:load", function () {
  setTimeout(jdgmTriggerRelayout, 200);
});
document.addEventListener("jdgm.loadedReviewsGridWidget", function () {
  setTimeout(jdgmTriggerRelayout, 200);
});

// Image-load fallback: wait for all review images to finish loading
window.addEventListener("load", function () {
  setTimeout(function () {
    if (!jdgmTriggerRelayout()) {
      // Widget may still be rendering — retry a couple of times
      setTimeout(jdgmTriggerRelayout, 800);
      setTimeout(jdgmTriggerRelayout, 1600);
    }
  }, 400);
});

/* ------------------------------------------------------------------ *
 * Mystery-tattoo free-gift safety net (P0-1 / P0-2)
 *
 * The free "Mystery Tattoo" gift (variant 53380878696778, EUR 14.90) is
 * added by the EG Auto Add to Cart app when the cart crosses the
 * free-shipping / gift threshold. The app's removal is intermittent: when
 * the cart drops back below the threshold the gift can stay in the cart
 * at full price and resist removal (P0-1).
 *
 * This net recomputes on EVERY cart change. If a gift line is present
 * while the qualifying subtotal is below the threshold, it removes the
 * gift line. The qualifying subtotal deliberately EXCLUDES the gift, so a
 * full-price gift can never inflate the total back above the threshold
 * and mask the problem. The threshold is read live from the free-shipping
 * bar so it follows the shopper's market; if it can't be determined the
 * net does nothing rather than guess.
 * ------------------------------------------------------------------ */
(function () {
  "use strict";

  var GIFT_VARIANT_ID = 53380878696778; // "Mystery Tattoo - Geschenk"
  var GIFT_PRODUCT_ID = 15280581378378;
  var busy = false;
  var debounceTimer = null;

  function shopRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  }

  // Current market's threshold (in cents), from the free-shipping bar.
  function getThreshold() {
    var el = document.querySelector("[data-free-shipping-amount]");
    if (!el) return null;
    var amount = parseInt(el.getAttribute("data-free-shipping-amount"), 10);
    return amount > 0 ? amount : null;
  }

  function isGift(item) {
    return (
      item.variant_id === GIFT_VARIANT_ID || item.product_id === GIFT_PRODUCT_ID
    );
  }

  function qualifyingTotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isGift(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  function refreshCartUI() {
    document.dispatchEvent(
      new CustomEvent("on:cart:change", { bubbles: true, cancelable: false })
    );
  }

  function checkGift() {
    if (busy) return;
    var threshold = getThreshold();
    if (!threshold) return; // threshold unknown — never guess

    busy = true;
    fetch(shopRoot() + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var gift = cart.items.find(isGift);
        if (!gift || qualifyingTotal(cart) >= threshold) {
          busy = false;
          return null;
        }
        // Below threshold with the gift still present — remove it.
        return fetch(shopRoot() + "cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ id: gift.key, quantity: 0 }),
        })
          .then(function (res) {
            return res.json();
          })
          .then(function () {
            busy = false;
            refreshCartUI();
          });
      })
      .catch(function () {
        busy = false;
      });
  }

  function scheduleCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(checkGift, 250);
  }

  document.addEventListener("on:cart:change", scheduleCheck);
  document.addEventListener("on:cart:after-merge", scheduleCheck);

  // Also run once on entry, in case the shopper lands with a stuck gift.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", checkGift);
  } else {
    checkGift();
  }
})();

/* ------------------------------------------------------------------ *
 * P1-1 / P0-5: global quick-add for product-card cart icons.
 *
 * The `.cart-icons` button appears on product cards on collection pages AND
 * inside the cart-drawer cross-sells ("Shop the Look"). It used to be wired
 * up only on the collection template, so inside the drawer it did nothing.
 * This delegated listener makes it add the product from anywhere and open the
 * cart drawer instead of navigating to the PDP / legacy /cart, keeping the
 * shopper in the buy flow.
 * ------------------------------------------------------------------ */
(function () {
  "use strict";

  /**
   * Build and show the "added to cart" toast from the shared #AddedNotification
   * template (rendered by theme.liquid whenever quick-buy is enabled). The
   * <added-notification> element auto-displays on connect and reads the product
   * title from data-product-title. Returns the element, or null if the template
   * isn't present (caller then falls back to opening the drawer).
   */
  function showAddedToast(productTitle, productImage) {
    var template = document.getElementById("AddedNotification");
    if (!template || !template.content || !template.content.firstElementChild) {
      return null;
    }
    var notification = template.content.firstElementChild.cloneNode(true);
    notification.dataset.productTitle = productTitle || "";
    if (productImage) notification.dataset.productImage = productImage;
    document.body.appendChild(notification);
    return notification;
  }

  document.addEventListener("click", function (evt) {
    var icon = evt.target.closest(".cart-icons");
    if (!icon) return;

    // The icon sits inside the product-card link; stop the card navigation.
    evt.preventDefault();
    evt.stopPropagation();

    if (icon.classList.contains("is-loading")) return;
    var variantId = icon.getAttribute("proid");
    if (!variantId) return;

    // Product-card quick-adds (PLP grid, search) confirm with the "added" toast
    // instead of popping the drawer, so a shopper can add several items in a row
    // without the drawer interrupting each time ("mass buying"). The SAME button
    // inside the cart drawer (cross-sell "Shop the Look") keeps opening/refreshing
    // the drawer, since the shopper is already in it.
    var inDrawer = !!icon.closest("cart-drawer, .js-cart-drawer");

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    icon.classList.add("is-loading");
    // Keep the spinner up for a perceptible minimum even when the add resolves
    // instantly, so the click always registers visually before the feedback.
    var startedAt = Date.now();
    var MIN_SPINNER_MS = 450;
    // shop statistics (assets/tt-stats.js): which product was added, from the drawer band or a card, toast shown
    var card = icon.closest("[data-handle]");
    var announce = function (toast) {
      try {
        document.dispatchEvent(new CustomEvent("tt:quick-add", {
          detail: { handle: card ? card.getAttribute("data-handle") : "", inDrawer: inDrawer, toast: toast }
        }));
      } catch (e) {}
    };
    var settle = function (added, lineItem) {
      var elapsed = Date.now() - startedAt;
      var wait = Math.max(0, MIN_SPINNER_MS - elapsed);
      setTimeout(function () {
        icon.classList.remove("is-loading");
        if (!added) return;

        // Always refresh the header cart count / drawer contents in the
        // background (this does NOT open the drawer on its own).
        document.dispatchEvent(
          new CustomEvent("on:cart:change", { bubbles: true })
        );
        // The header refresh above only knows the old header markup, so the count bubbles of the top bar, the
        // header and the condensed search + cart bar stayed stale until a reload. One /cart.js request per add
        // sets every .cart-link__count (empty = no bubble, like the Liquid output).
        fetch(root + "cart.js", { headers: { Accept: "application/json" } })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (cart) {
            if (!cart) return;
            document.querySelectorAll(".cart-link__count").forEach(function (el) {
              el.textContent = cart.item_count > 0 ? String(cart.item_count) : "";
            });
          })
          .catch(function () {});

        if (inDrawer) {
          announce(false);
          document.dispatchEvent(
            new CustomEvent("dispatch:cart-drawer:open", { bubbles: true })
          );
          return;
        }

        // Product card: show the toast. Fall back to opening the drawer only if
        // the toast template isn't on the page, so feedback is never lost.
        var shown = showAddedToast(
          lineItem && (lineItem.product_title || lineItem.title),
          lineItem && (lineItem.featured_image && lineItem.featured_image.url || lineItem.image)
        );
        announce(!!shown);
        if (!shown) {
          document.dispatchEvent(
            new CustomEvent("dispatch:cart-drawer:open", { bubbles: true })
          );
        }

        // Flash the localized "added" label ("Hinzugefügt" on DE) on the button
        // itself as inline confirmation, then restore the original. Only buttons
        // that carry both a label span and a localized data-label-added qualify
        // (product cards); the icon-only drawer/search variants have neither, so
        // they are skipped. The flag guards against overlapping flips on a rapid
        // second click.
        var labelEl = icon.querySelector(".cart-icons__label");
        var addedText = icon.getAttribute("data-label-added");
        if (labelEl && addedText && !icon.dataset.addedFlashing) {
          icon.dataset.addedFlashing = "1";
          icon.classList.add("is-added");
          var originalLabel = labelEl.textContent;
          labelEl.textContent = addedText;
          setTimeout(function () {
            labelEl.textContent = originalLabel;
            icon.classList.remove("is-added");
            delete icon.dataset.addedFlashing;
          }, 1600);
        }
      }, wait);
    };

    fetch(root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      // adds from the cart drawer band carry the hidden line property _source = drawer (not shown in checkout / emails;
      // counted in the dashboard «Statistik» from the orders). The band never shows a tattoo that is already in the cart,
      // so this does not split an existing line. assets/tt-eta.js adds the delivery property on top.
      body: JSON.stringify(
        inDrawer
          ? { id: variantId, quantity: 1, properties: { _source: "drawer" } }
          : { id: variantId, quantity: 1 }
      ),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        settle(true, data);
      })
      .catch(function () {
        settle(false);
      });
  });
})();

/* ------------------------------------------------------------------ *
 * P2-6: PDP checkout button + free-shipping / mystery-gift nudge.
 *
 * Opt-in (buy-buttons block setting, default off). The button links to
 * the cart by default; this delegated listener intercepts the click,
 * reads the live cart total and compares it to the market's
 * free-shipping threshold (rendered as data-threshold, in cents, by the
 * same logic as free-shipping-bar.liquid). Below the threshold it
 * reveals the nudge and keeps the shopper on the page so they can add
 * more; at or above it sends them straight to checkout. If anything
 * fails it falls back to the button's own href (the cart).
 * ------------------------------------------------------------------ */
(function () {
  "use strict";

  document.addEventListener("click", function (evt) {
    var btn = evt.target.closest("[data-pdp-checkout]");
    if (!btn) return;

    var threshold = parseInt(btn.getAttribute("data-threshold"), 10);
    // No threshold configured for this market — let the link behave normally.
    if (!threshold) return;

    evt.preventDefault();

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    var checkoutUrl = btn.getAttribute("data-checkout-url") || "/checkout";
    var fallbackUrl = btn.getAttribute("href") || root + "cart";

    fetch(root + "cart.js", { headers: { Accept: "application/json" } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        // KI credit packs (no shipping) never count towards the free-shipping threshold
        var shipTotal = (cart.items || []).reduce(function (sum, it) { return it.requires_shipping === false ? sum - it.final_line_price : sum; }, cart.total_price);
        if (shipTotal < threshold) {
          var nudge = btn.parentNode.querySelector("[data-pdp-checkout-nudge]");
          if (nudge) nudge.hidden = false;
        } else if (window.TTEta) {
          // refresh the delivery property of every line first (assets/tt-eta.js)
          window.TTEta.beforeCheckout().then(function () { window.location.href = checkoutUrl; });
        } else {
          window.location.href = checkoutUrl;
        }
      })
      .catch(function () {
        window.location.href = fallbackUrl;
      });
  });
})();


/* Cart delivery estimate: moved to assets/tt-eta.js (one calculator for PDP, drawer, cart page and the line
   item property; config = shop metafield tattup.delivery). */

/* ------------------------------------------------------------------ *
 * PLP sticky cart bar (mobile collection/search).
 *
 * The bar (sections/plp-sticky-cart.liquid, in the overlay group) shows the
 * free-shipping progress + tattoo count + total and opens the cart drawer on
 * tap. It's server-rendered (correct money format + localisation across every
 * market), and kept in sync on cart change by re-fetching its own section via
 * the Section Rendering API and swapping the markup in place — the same pattern
 * the header cart-count uses. Only wired up on collection/search pages, where
 * the bar is actually shown.
 * ------------------------------------------------------------------ */
(function () {
  "use strict";

  // Runs on EVERY template: the pill itself is only shown on
  // collection/search (CSS gate in global.css), but the fetched section is
  // also the refresh source for the PDP merged sticky bar
  // (snippets/sticky-reward-atc.liquid), whose [data-sticky-reward-status]
  // gets the fresh [data-reward-cart-status] markup copied in below.

  // Whole bar opens the cart drawer (delegated, so it survives the refresh
  // that replaces the element).
  function openDrawer() {
    document.dispatchEvent(
      new CustomEvent("dispatch:cart-drawer:open", { bubbles: true })
    );
  }
  // The bar contains its own interactive children (e.g. the reward picker's
  // "Choose Free Tattoos" button). Those must keep their own behaviour — only
  // clicks on the bar's inert surface open the drawer. Same for the PDP sticky
  // reward bar (snippets/sticky-reward-atc.liquid): its free surface opens the
  // drawer, while Add to Cart (inside the product form) and the picker button
  // keep working as before.
  function isBarSurface(target) {
    if (!target || !target.closest) return false;
    if (!target.closest("[data-plp-sticky-cart], .sticky-reward-atc")) return false;
    return !target.closest("button, a, input, select, textarea, label, form, [data-reward-picker-open]");
  }
  document.addEventListener("click", function (evt) {
    if (isBarSurface(evt.target)) openDrawer();
  });
  document.addEventListener("keydown", function (evt) {
    if (evt.key !== "Enter" && evt.key !== " ") return;
    if (isBarSurface(evt.target)) {
      evt.preventDefault();
      openDrawer();
    }
  });

  // Re-render the bar from the server on any cart change so the count, total and
  // free-shipping progress stay correct without any client-side money math.
  var refreshing = false;
  function refresh() {
    var bar = document.querySelector("[data-plp-sticky-cart]");
    if (!bar || refreshing) return;
    var id = bar.getAttribute("data-section-id");
    if (!id) return;
    refreshing = true;
    fetch(window.location.pathname + "?sections=" + encodeURIComponent(id))
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var html = data[id];
        if (!html) return;
        var tmp = document.createElement("template");
        tmp.innerHTML = html;
        var fresh = tmp.content.querySelector("[data-plp-sticky-cart]");

        // Grab the status markup BEFORE the pill node is moved out of the
        // template by replaceWith below.
        var freshStatus = tmp.content.querySelector("[data-reward-cart-status]");
        var freshStatusCopy = freshStatus ? freshStatus.cloneNode(true) : null;

        var current = document.querySelector("[data-plp-sticky-cart]");
        if (fresh && current) current.replaceWith(fresh);

        // Keep the PDP merged sticky bar's status block (progress + count +
        // total + savings) in sync from the same server render.
        if (freshStatusCopy) {
          document
            .querySelectorAll("[data-sticky-reward-status] [data-reward-cart-status]")
            .forEach(function (el) {
              el.replaceWith(freshStatusCopy.cloneNode(true));
            });
        }
      })
      .catch(function () {})
      .finally(function () {
        refreshing = false;
      });
  }

  document.addEventListener("on:cart:change", refresh);
  document.addEventListener("on:cart:after-merge", refresh);
})();

/**
 * =================================================================================================
 * <media-swap-toggle> — PLP design-view switch (2026-09-04)
 * =================================================================================================
 *
 * Swaps every product card's first image with its hover image, so the collection grid shows the
 * standalone designs (white background) and hovering brings back the on-model photo.
 *
 * The switch is inverted relative to the swap: ON (default) = "Display", the on-model photos;
 * OFF = "Designs", i.e. `tt-media-swap` is on <html>. Labels and switch visuals are CSS-only.
 *
 * The whole swap is a single class on <html> (`tt-media-swap`) — see global.css. Nothing is
 * fetched, nothing is re-rendered and no per-card JS runs, so the cost of a toggle is one class
 * mutation plus a compositor-only cross-fade of images that are already painted.
 *
 * `tt-swapping` is added around the flip for the longer, staggered animation and removed again
 * afterwards, so hover keeps the theme's 100ms transition and no card is left with will-change.
 *
 * State is kept in sessionStorage so it survives a trip to a product page and back. It is applied
 * as this file parses (deferred, before DOMContentLoaded) rather than on DOMContentLoaded, so the
 * grid is already in the chosen mode by the time the cards paint.
 *
 * Broadcasts `on:media-swap:change` with `{ active }` — the mobile sticky copy of the utility bar
 * (a clone made by FilterContainer) uses it to keep its own switch in sync.
 */
(function () {
  var SWAP_CLASS = "tt-media-swap";
  var ANIM_CLASS = "tt-swapping";
  var PREP_CLASS = "tt-swap-prep";
  var STORAGE_KEY = "tt:media-swap";
  var CHANGE_EVENT = "on:media-swap:change";
  var ANIM_MS = 750;
  var root = document.documentElement;
  var animTimer = null;

  function isActive() {
    return root.classList.contains(SWAP_CLASS);
  }

  function store(active) {
    try {
      sessionStorage.setItem(STORAGE_KEY, active ? "1" : "0");
    } catch (e) {
      // private mode / storage disabled — the switch still works for this page
    }
  }

  // Owner fix 2026-10-19: a card whose photo was changed by hand (arrows / swipe) first goes back to
  // its initial state (1st image = skin, 2nd = design), so the switch shows the same kind of image on
  // every card. Runs on every press over the cards in the DOM at that moment, so cards added later by
  // filtering / load more are covered too (they start in the initial state anyway). Cards that the
  // theme has not initialised yet (no .images) are untouched and therefore still in the initial state.
  function resetCards() {
    document.querySelectorAll("product-block").forEach(function (block) {
      var imgs = block.images;
      if (!imgs || imgs.length < 2 || typeof block.setActiveImage !== "function") return;
      if (
        imgs[0].classList.contains("product-block__image--active") &&
        imgs[1].classList.contains("product-block__image--show-on-hover")
      ) return;
      block.setActiveImage(0);
    });
  }

  function setActive(active, animate) {
    if (active === isActive()) return;

    if (animate) {
      // FLIP: park the hidden image at its start scale with transitions off (PREP), flush that
      // style, then release it in the same frame as the swap so the incoming image has a real
      // scale to ease down *from* while it fades in. Without the flush both changes coalesce
      // into one recalc and the zoom never happens.
      root.classList.add(ANIM_CLASS, PREP_CLASS);
      resetCards(); // with transitions off: hand-changed cards jump back to their first image at once
      void root.offsetHeight; // eslint-disable-line no-unused-expressions -- forced style flush
      root.classList.remove(PREP_CLASS);
      root.classList.toggle(SWAP_CLASS, active);

      clearTimeout(animTimer);
      animTimer = setTimeout(function () {
        root.classList.remove(ANIM_CLASS);
      }, ANIM_MS);
    } else {
      resetCards();
      root.classList.toggle(SWAP_CLASS, active);
    }

    store(active);
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { active: active } })
    );
  }

  // Restore before the grid paints. Gated on the switch actually being on the page, so the
  // stored preference can never strand a shopper on a template that has no way to turn it off
  // (product page related products, search, the header menu cards...).
  try {
    if (
      sessionStorage.getItem(STORAGE_KEY) === "1" &&
      document.querySelector("media-swap-toggle")
    ) {
      root.classList.add(SWAP_CLASS);
    }
  } catch (e) {
    // storage disabled — start from the theme default
  }

  var MediaSwapToggle = class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector(".media-swap__btn");
      if (!this.button) return;

      this.boundClick = this.handleClick.bind(this);
      this.boundSync = this.handleChange.bind(this);
      this.button.addEventListener("click", this.boundClick);
      document.addEventListener(CHANGE_EVENT, this.boundSync);
      this.sync(isActive());
    }

    disconnectedCallback() {
      if (!this.button) return;
      this.button.removeEventListener("click", this.boundClick);
      document.removeEventListener(CHANGE_EVENT, this.boundSync);
    }

    handleClick() {
      setActive(!isActive(), true);
    }

    handleChange(evt) {
      this.sync(evt.detail.active);
    }

    // aria only — the visual state comes from the root class, so every copy of the utility bar
    // (including the cloned mobile sticky one) is always in sync for free. The switch is "on"
    // for the default photo view, so it's checked while the design swap is NOT active.
    sync(active) {
      this.button.setAttribute("aria-checked", active ? "false" : "true");
    }
  };

  if (!window.customElements.get("media-swap-toggle")) {
    window.customElements.define("media-swap-toggle", MediaSwapToggle);
  }
})();

// === Header scrolls away with the page (2026-10-18, all pages except product pages) ===
// The full header (logo + menu) is no longer pinned and slid away – it simply scrolls out with the content, so no
// empty band is left above the page. body.header-out is set once it has left the viewport (below the pinned
// announcement bar); the condensed mobile search + cart bar is shown exactly then (global.css "HEADER SCROLLS AWAY").
(function () {
  var body = document.body;
  if (!body || body.classList.contains("template-product")) return;
  var header = document.querySelector(".section-header");
  if (!header || !("IntersectionObserver" in window)) return;
  var annH = function () {
    var a = document.querySelector(".section-announcement-bar");
    return a ? Math.round(a.getBoundingClientRect().height) : 0;
  };
  var io;
  var watch = function () {
    if (io) io.disconnect();
    io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      body.classList.toggle("header-out", !e.isIntersecting && e.boundingClientRect.top < 0);
    }, { rootMargin: "-" + annH() + "px 0px 0px 0px", threshold: 0 });
    io.observe(header);
  };
  watch();
  window.addEventListener("resize", watch);
})();

// === Lazy video posters (2026-10-20, all pages) ===
// <video data-poster="…"> gets its poster only shortly before it scrolls into view: a poster attribute is downloaded
// with the page, even far below the fold (community reels, about us, blog videos). Sections with their own video
// script may set it earlier themselves; this is the shared fallback.
(function () {
  var vids = document.querySelectorAll("video[data-poster]");
  if (!vids.length) return;
  var set = function (v) { if (v.getAttribute("data-poster")) { v.poster = v.getAttribute("data-poster"); v.removeAttribute("data-poster"); } };
  if (!("IntersectionObserver" in window)) { [].forEach.call(vids, set); return; }
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) { if (e.isIntersecting) { set(e.target); io.unobserve(e.target); } });
  }, { rootMargin: "600px 600px" });
  [].forEach.call(vids, function (v) { io.observe(v); });
})();

// === FILTER DRAWER (owner 19.10.2026): own window on every width (styles: global.css «FILTER DRAWER ON ALL WIDTHS") ===
// The theme (main.js FilterContainer) opens / closes it by toggling filter-container--show-filters-mobile via any
// [data-toggle-filters] element (Filter button, ×, backdrop). Added here: ESC closes, focus moves to the × on open and
// back to the Filter button on close, and the page behind does not scroll (wheel / touch on the backdrop is eaten, the
// drawer does not hand its scroll on to the page) – nothing to undo on close, so the page scrolls normally again.
(function () {
  var OPEN = "filter-container--show-filters-mobile";
  var opener = null;
  var wasOpen = false;
  var isOpen = function (c) { return !!(c && c.classList.contains(OPEN)); };
  var filterButton = function () {
    if (opener && opener.isConnected && opener.offsetParent) return opener;
    return document.querySelector(".utility-bar:not(.utility-bar--sticky-mobile-copy) .toggle-btn[data-toggle-filters]");
  };
  var focusEl = function (el) { if (el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } } };
  function sync(c) {
    var open = isOpen(c);
    if (open === wasOpen) return;
    wasOpen = open;
    document.documentElement.classList.toggle("tt-filter-open", open);
    if (open) {
      setTimeout(function () { if (isOpen(c)) focusEl(c.querySelector(".filters__close")); }, 80);
    } else {
      focusEl(filterButton());
    }
  }
  function init() {
    var c = document.querySelector("filter-container");
    if (!c || c.__ttDrawer) return;
    c.__ttDrawer = true;
    new MutationObserver(function () { sync(c); }).observe(c, { attributes: true, attributeFilter: ["class"] });
    document.addEventListener("click", function (e) {
      var t = e.target.closest && e.target.closest(".toggle-btn[data-toggle-filters]");
      if (t) opener = t;
    }, true);
    document.addEventListener("keydown", function (e) {
      if ((e.key === "Escape" || e.key === "Esc") && isOpen(c)) {
        var x = c.querySelector(".filters__close") || c.querySelector(".filter-shade");
        if (x) { e.preventDefault(); x.click(); }
      }
    });
    var shade = c.querySelector(".filter-shade");
    if (shade) {
      var eat = function (e) { if (isOpen(c)) e.preventDefault(); };
      shade.addEventListener("wheel", eat, { passive: false });
      shade.addEventListener("touchmove", eat, { passive: false });
    }
    var panel = c.querySelector(".filters");
    if (panel) {
      panel.addEventListener("wheel", function (e) {
        if (!isOpen(c)) return;
        var up = e.deltaY < 0;
        if ((up && panel.scrollTop <= 0) || (!up && panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1)) e.preventDefault();
      }, { passive: false });
    }
    sync(c);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

/* Shop the Look statistics – looks this visitor opened (Looks dashboard «Statistik», tattup-own-apps).
   ONLY with analytics consent (Shopify customerPrivacy, set by the Pandectes banner): the handles of the looks
   opened in the last 30 days (max 10) stay in localStorage and go into the hidden cart attribute _viewed_looks,
   so an order shows whether its tattoos were bought after looking at a look. Without consent nothing is stored
   (an existing list is removed). assets/stl-look.js calls add(handle) when a look window opens; every add to cart
   (on:cart:add) re-checks the attribute, e.g. for a new cart. No ids, no cookies of our own. */
(function () {
  if (window.TattupLooksSeen) return;
  var KEY = "tattup_viewed_looks", MARK = "tattup_vl_sync", DAYS = 30, MAX = 10;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  function allowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && typeof cp.analyticsProcessingAllowed === "function" && cp.analyticsProcessingAllowed());
    } catch (e) { return false; }
  }
  function read() {
    try {
      var cut = Date.now() - DAYS * 864e5;
      var l = JSON.parse(localStorage.getItem(KEY) || "[]");
      return (Array.isArray(l) ? l : []).filter(function (x) { return x && x.t > cut && /^[a-z0-9][a-z0-9-]{0,99}$/.test(x.h); }).slice(0, MAX);
    } catch (e) { return []; }
  }
  function forget() { try { localStorage.removeItem(KEY); sessionStorage.removeItem(MARK); } catch (e) {} }
  function sync() {
    if (!allowed()) { forget(); return; }
    var v = read().map(function (x) { return x.h; }).join(",");
    if (!v) return;
    var cart = (document.cookie.match(/(?:^|;\s*)cart=([^;]+)/) || [])[1] || "";
    var mark = v + "|" + cart;
    try { if (sessionStorage.getItem(MARK) === mark) return; } catch (e) {}
    fetch(root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { _viewed_looks: v } })
    }).then(function (r) {
      if (!r.ok) return;
      var c2 = (document.cookie.match(/(?:^|;\s*)cart=([^;]+)/) || [])[1] || "";
      try { sessionStorage.setItem(MARK, v + "|" + c2); } catch (e) {}
    }).catch(function () {});
  }
  function add(h) {
    if (!allowed()) { forget(); return; }
    if (!/^[a-z0-9][a-z0-9-]{0,99}$/.test(String(h || ""))) return;
    var l = read().filter(function (x) { return x.h !== h; });
    l.unshift({ h: h, t: Date.now() });
    try { localStorage.setItem(KEY, JSON.stringify(l.slice(0, MAX))); } catch (e) { return; }
    sync();
  }
  window.TattupLooksSeen = { add: add, sync: sync };
  document.addEventListener("on:cart:add", function () { setTimeout(sync, 0); });
})();

/* tt-empty-search: the magnifier / Enter in a search field with nothing typed only puts the cursor into the field
   instead of opening an empty search page (owner 20.10.2026). Applies to every search form of the shop. */
document.addEventListener('submit', function (e) {
  var form = e.target;
  if (!form || !form.getAttribute) return;
  var action = form.getAttribute('action') || '';
  if (!/\/search\/?(\?|$)/.test(action)) return;
  var q = form.querySelector('input[name="q"]');
  if (q && !q.value.trim()) { e.preventDefault(); q.value = ''; q.focus(); }
}, true);
